import React, { memo, useState, useEffect } from 'react'
import { isSameDay, format } from 'date-fns'
import { de, fr } from 'date-fns/locale'
import i18n from 'atpjs/helpers/i18n'
import Branches from 'tve/components/appointment_grid/desktop/branches'
import Rows from 'tve/components/appointment_grid/desktop/rows'
import { useLegendsByDate } from 'tve/components/appointment_grid/desktop/hooks'
import WeekDisplay from 'tve/components/appointment_grid/week_display'
import { DAY_PATTERN } from 'tve/helpers/date'

const EXTERNAL_CLASSNAME = 'navbar-fixed-top'
const t = i18n.prefix(['components', 'appointmentGrid'])

const useScroll = () => {
  const [bodyOffset, setBodyOffset] = useState(document.body.getBoundingClientRect())
  const [scrollY, setScrollY] = useState(bodyOffset.top)

  const listener = (_e) => {
    setBodyOffset(document.body.getBoundingClientRect())
    setScrollY(-bodyOffset.top)
  }

  useEffect(() => {
    window.addEventListener('scroll', listener)
    return () => {
      window.removeEventListener('scroll', listener)
    }
  }, [])

  return {
    scrollY
  }
}

const dateLocale = {
  de,
  fr
}

const Desktop = memo(
  ({
    currentDate,
    grid,
    nextHandler,
    previousHandler,
    handleBranchId,
    branchId,
    branchValues,
    value,
    onChange,
    selectBranch
  }) => {
    const startCell = grid[0][0]
    const endCell = grid[0][grid[0].length - 1]

    // we need this to trigger a rerendering on scroll
    useScroll()

    const headerHeight = (document.getElementsByClassName(EXTERNAL_CLASSNAME)[0] || { clientHeight: 200 }).clientHeight
    const wrapper = document.getElementById('atp-time-header-wrapper-el')
    const distanceToTop = wrapper ? wrapper.getBoundingClientRect().top : 0
    const widgetStartToHeaderDistance = wrapper ? headerHeight : 200
    const headerOffset = distanceToTop - widgetStartToHeaderDistance
    const wrapperHeight = wrapper ? wrapper.getBoundingClientRect().height : 500
    const wrapperWidth = wrapper ? wrapper.getBoundingClientRect().width : 500
    const distanceToLeft = wrapper ? wrapper.getBoundingClientRect().left : 200
    const hasScrolledPast = -1 * headerOffset + headerHeight - wrapperHeight > 0

    const timeHeaderWrapper = document.getElementsByClassName('atp-time-header-wrapper')[0]
    const timeHeaderWrapperHeight = timeHeaderWrapper ? timeHeaderWrapper.offsetHeight : 0

    const [legends, addLegend] = useLegendsByDate(currentDate)

    return (
      <div id="atp-time-header-wrapper-el">
        {branchValues.length > 2 && <p className="atp-margin-top-s atp-margin-bottom-s">{t(['whichBranch'])}</p>}

        {!hasScrolledPast && (
          <div
            style={
              wrapper
                ? {
                    left: distanceToLeft,
                    width: wrapperWidth,
                    top: headerOffset < 0 ? widgetStartToHeaderDistance : distanceToTop
                  }
                : {}
            }
            className={
              wrapper && headerOffset < 0
                ? 'atp-time-header-wrapper atp-time-header-wrapper--fixed'
                : 'atp-time-header-wrapper'
            }>
            <Branches handleBranchId={handleBranchId} branchId={branchId} branchValues={branchValues} />
            <div className="atp-time-week">
              <button
                type="button"
                className="atp-time-week__prev"
                disabled={!previousHandler}
                onClick={previousHandler}>
                &lt;
              </button>
              <WeekDisplay startCell={startCell} endCell={endCell} />
              <button type="button" className="atp-time-week__next" disabled={!nextHandler} onClick={nextHandler}>
                &gt;
              </button>
            </div>
            <table className="atp-time-table">
              <thead>
                <tr>
                  <th>&nbsp;</th>
                  {grid[0].map((cell, columnIndex) => {
                    const slotLength = grid.reduce((sum, row) => {
                      return row[columnIndex].available ? sum + 1 : sum
                    }, 0)
                    const classes = ['atp-time-table__day']
                    if (slotLength === 0) classes.push('atp-time-table__day--inactive')
                    if (isSameDay(new Date(), cell.start)) classes.push('atp-time-table__day--today')
                    return (
                      <th key={format(cell.start, 'yyyy-MM-dd')}>
                        <span className="atp-time-table__day__name">
                          {format(cell.start, 'EEEEEE', { locale: dateLocale[i18n.locale] })}
                        </span>
                        <span className="atp-time-table__day__date">
                          {format(cell.start, DAY_PATTERN[i18n.locale])}
                        </span>
                      </th>
                    )
                  })}
                </tr>
              </thead>
            </table>
          </div>
        )}

        <table
          className="atp-time-table"
          style={{
            paddingTop: headerOffset < 0 ? timeHeaderWrapperHeight : 0
          }}>
          <tbody>
            <Rows
              grid={grid}
              branchId={branchId}
              value={value}
              onChange={onChange}
              selectBranch={selectBranch}
              addLegend={addLegend}
              legends={legends}
            />
          </tbody>
        </table>
        {legends.length > 0 && (
          <ol className="atp-timetable-legend">
            {legends.map((entry, i) => (
              <li key={i} className="atp-timetable-legend__entry">
                {entry.holidayName}
              </li>
            ))}
          </ol>
        )}
      </div>
    )
  }
)
Desktop.displayName = 'Desktop'

export default Desktop
